import { Config } from "@/configs/config";
import { authAPI } from "@/services/api";
import { useRouter } from "vue-router";
import { PLAN_NAMES } from "@/components/OnePricingPlan.vue";
import { SYNC_STATUS } from "@/types/sync-status";
import { aliasAndIdentifyUserId } from "@/analytics/mixpanel";

const MINIMAL_SCOPES = "openid email profile";
const ADVANCED_SCOPES =
  "https://www.googleapis.com/auth/gmail.settings.basic https://www.googleapis.com/auth/gmail.modify";

function buildAuthUrl(scope, prompt, loginHint = "") {
  const authUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  authUrl.searchParams.append("response_type", "code");
  authUrl.searchParams.append("client_id", Config.GOOGLE_CLIENT_ID);
  authUrl.searchParams.append("redirect_uri", Config.REDIRECT_URI);
  authUrl.searchParams.append("scope", scope);
  authUrl.searchParams.append("access_type", "offline");
  authUrl.searchParams.append("prompt", prompt);

  if (loginHint) {
    authUrl.searchParams.append("login_hint", loginHint);
  }

  return authUrl;
}

async function pollPopupForAuthCode(popup) {
  return new Promise((resolve) => {
    const pollTimer = setInterval(() => {
      try {
        if (!popup || popup.closed) {
          clearInterval(pollTimer);
          console.log("Popup closed by user.");
          resolve(null);
          return;
        }
        if (popup.location.href.startsWith(Config.REDIRECT_URI)) {
          clearInterval(pollTimer);
          const urlParams = new URLSearchParams(popup.location.search);
          const authCode = urlParams.get("code");
          if (authCode) {
            resolve(authCode);
          } else {
            resolve(null);
          }
        }
      } catch (error) {
        // Ignore cross-origin errors
      }
    }, 500);
  });
}

export async function openPopup(isAdvanced = false, userEmail = "") {
  const width = 500;
  const height = 600;
  const left = (window.screen.width - width) / 4;
  const top = (window.screen.height - height) / 4;

  // Build appropriate URL based on scope type
  const scope = isAdvanced
    ? `${MINIMAL_SCOPES} ${ADVANCED_SCOPES}`
    : MINIMAL_SCOPES;
  const prompt = isAdvanced ? "consent" : "select_account";
  const url = buildAuthUrl(scope, prompt, userEmail);

  const popup = window.open(
    url,
    "GoogleAuthPopup",
    `width=${width},height=${height},top=${top},left=${left}`
  );

  // Wait for auth code
  const authCode = await pollPopupForAuthCode(popup);
  return { popup, authCode };
}

export function getMinimalScopesUrl() {
  return buildAuthUrl(MINIMAL_SCOPES, "select_account");
}

export function getAdvancedScopesUrl(userEmail) {
  return buildAuthUrl(
    `${MINIMAL_SCOPES} ${ADVANCED_SCOPES}`,
    "consent",
    userEmail
  );
}

export const useGoogleAuth = () => {
  const router = useRouter();

  const registerUser = async (authCode, popup, planType) => {
    try {
      const response = await authAPI.login(authCode);

      if (
        response.data.already_registered &&
        ![SYNC_STATUS.NOT_SYNCED].includes(response.data.sync_status)
      ) {
        router.push("/account");
      } else {
        const { popup: newPopup, authCode } = await openPopup(
          true,
          response.data.address
        );
        popup = newPopup;
        if (authCode) {
          const response = await handleRegistrationResponse(authCode, planType);

          if (planType !== PLAN_NAMES.FREE) {
            router.push({
              path: "/payment",
              query: {
                address: response.data.address,
                userId: response.data.user_id,
              },
            });
          } else {
            router.push("/account");
          }
        }
      }

      popup.close();
    } catch (error) {
      popup.close();
      console.error("Error verifying user existence:", error);
    }
  };

  const verifyUserExists = async (authCode, popup) => {
    let response;

    try {
      response = await authAPI.login(authCode);

      if (
        response.data.already_registered &&
        ![SYNC_STATUS.NOT_SYNCED].includes(response.data.sync_status)
      ) {
        router.push("/account");
      } else {
        router.push("/plans?fromSignIn=true&address=" + response.data.address);
      }

      popup?.close();
    } catch (error) {
      if (error.response.status === 401) {
        const { popup, authCode } = await openPopup(
          true,
          error.response.data.details.data.address
        );
        if (authCode) {
          await handleRegistrationResponse(authCode);
          router.push("/account");
        }

        popup?.close();
      }
    }
  };

  const handleRegistrationResponse = async (authCode, planType) => {
    try {
      const response = await authAPI.sync(authCode, planType);
      const userId = response?.data?.user_id;
      if (userId) aliasAndIdentifyUserId(userId);
      return response;
    } catch (error) {
      console.error("Error during registration:", error);
    }
  };

  const handleGoogleSignIn = async () => {
    const { popup, authCode } = await openPopup(false, "");
    if (authCode) {
      await verifyUserExists(authCode, popup);
    }
  };

  const handleGoogleRegistration = async (planType, address) => {
    const { popup, authCode } = await openPopup(false, address);
    if (authCode) {
      await registerUser(authCode, popup, planType);
    }
  };

  return {
    handleGoogleSignIn,
    handleGoogleRegistration,
  };
};
